import React, { useEffect, useRef, useState } from 'react';
import { FaStar } from 'react-icons/fa';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

type Testimonial = {
  title: string;
  quote: string;
  author: string;
  stars: number;
  role?: string;
};

const testimonials: Testimonial[] = [
  {
    title: 'They have been an absolute pleasure to work with',
    quote:
      'I approached EPC Builder as we want to improve the EPC ratings of our rental properties and help our tenants reduce their energy bills. They have been an absolute pleasure to work with, our properties have been assessed, and we now have an improvement plan for each property clearly explaining how we can improve the EPC rating. Even better is that they checked what funding was available and some of our properties are being upgraded at no cost to us. I would have no reservations recommending them and in fact have already done so.',
    author: 'Kim Jackson',
    role: 'Riverview House Ltd',
    stars: 5,
  },
  {
    title: 'It’s a real weight off my mind',
    quote:
      'I’m very pleased with the service offered by EPC Builder, they assessed my house and provided a very detailed report outlining a couple of recommendations to make the house warmer and reduce my bills. They installed an air source heat pump and solar panels; it’s made a real difference not only to my bills but also how warm my house is. It’s a real weight off my mind not having to worry about whether I can afford to heat my house.',
    author: 'Peter Pugh',
    role: 'as seen on Channel 4',
    stars: 5,
  },
  {
    title: 'Fantastic service. Great communication',
    quote:
      'Fantastic service. Great communication through the whole process of applying for a grant for an upgrade to my grandmas 45 year old boiler. Really fast turnaround, installers clean and efficient. Would highly recommend.',
    author: 'Jordan Cain',
    role: 'Homeowner',
    stars: 5,
  },
  {
    title: 'I would recommend them to all',
    quote:
      'From start to finish Caroline Carrol and all the team from Improveasy worked really fast and professionally when arranging and installing my new boiler with thermostats on all the radiators. Everyone was so helpful, I would recommend them to all. Thank you so much! The fitters took all the waste and worked very fast during the installation.',
    author: 'Christine Robinson',
    role: 'Homeowner',
    stars: 5,
  },
  {
    title: 'This firm were very efficient with the work they did',
    quote:
      'This firm were very efficient with the work they did for me. They came on time and the work was completed the same day. I would recommend Improveasy for any work you need doing. A special thank you to Harry and Caroline who were very supportive and answered any questions I had.',
    author: 'Karen Riley',
    role: 'Homeowner',
    stars: 5,
  },
];

const Testimonials = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(2);

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth < 768) {
        setItemsPerPage(1);
      } else {
        setItemsPerPage(2);
      }
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const pageCount = itemsPerPage === 1 ? testimonials.length : Math.ceil(testimonials.length / itemsPerPage);
  const scrollToPage = (page: number) => {
    const container = containerRef.current;
    if (!container) return;

    const totalPages = pageCount;
    const clampedPage = Math.max(0, Math.min(page, totalPages - 1));
    const itemWidth = container.scrollWidth / testimonials.length;

    const isLastOddPage = itemsPerPage === 2 && testimonials.length % 2 === 1 && clampedPage === totalPages - 1;

    const scrollAmount = itemWidth * (isLastOddPage ? testimonials.length - 1 : clampedPage * itemsPerPage);

    container.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    setActiveIndex(clampedPage);
  };

  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      const itemWidth = container.scrollWidth / testimonials.length;
      const scrollLeft = container.scrollLeft;
      const currentPage = Math.round(scrollLeft / (itemWidth * itemsPerPage));
      setActiveIndex(currentPage);
    }
  };

  return (
    <div className="relative flex flex-col items-center px-4 py-8 lg:py-16">
      <div className="text-center lg:mb-12">
        <p className="mb-4 text-xs font-extrabold uppercase tracking-wider text-[#516B75] md:mb-0 lg:text-lg">
          TESTIMONIALS
        </p>
        <h2 className="text-2xl font-bold lg:text-5xl">
          From our{' '}
          <span className="font-kalam bg-gradient-to-l from-[#84AF7D] via-[#289A8C] to-[#214D55] bg-clip-text text-[32px] font-bold lowercase leading-[48px] text-transparent lg:text-[64px] lg:leading-[80px]">
            happy
          </span>{' '}
          customers
        </h2>
      </div>
      <div className="relative w-full max-w-6xl">
        <div
          ref={containerRef}
          onScroll={handleScroll}
          className="hide-scrollbar flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth px-4"
        >
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`flex snap-center flex-col items-start gap-6 md:p-6 lg:pt-12 ${
                itemsPerPage === 1 ? 'min-w-full' : 'min-w-[50%]'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="43" height="36" fill="none" viewBox="0 0 43 36">
                <path
                  d="M0 35.6012V23.9532C0 21.0518 0.298667 18.3212 0.896 15.7612C1.57867 13.2012 2.64533 10.6838 4.096 8.20916C5.632 5.73449 7.63733 3.25983 10.112 0.785156L16.768 6.03316C14.976 7.91049 13.5253 9.70249 12.416 11.4092C11.3067 13.0305 10.496 14.6945 9.984 16.4012C9.55733 18.0225 9.344 19.7292 9.344 21.5212L5.376 17.8092H17.92V35.6012H0ZM24.96 35.6012V23.9532C24.96 21.0518 25.2587 18.3212 25.856 15.7612C26.5387 13.2012 27.6053 10.6838 29.056 8.20916C30.592 5.73449 32.5973 3.25983 35.072 0.785156L41.728 6.03316C39.936 7.91049 38.4853 9.70249 37.376 11.4092C36.2667 13.0305 35.456 14.6945 34.944 16.4012C34.5173 18.0225 34.304 19.7292 34.304 21.5212L30.336 17.8092H42.88V35.6012H24.96Z"
                  fill="#2A9C8E"
                />
              </svg>
              <p className="font-jost text-[18px] font-semibold">{testimonial.title}</p>
              <p className="leading-relaxed">{testimonial.quote}</p>
              <div className="mt-auto flex flex-col">
                <p className="font-semibold">{testimonial.author}</p>
                {testimonial.role && <p className="text-sm italic text-[#516B75]">{testimonial.role}</p>}
                <div className="text-secondary mt-2 flex items-center gap-1">
                  {index >= testimonials.length - 3 ? (
                    <img src="/images/google-review-logo.png" alt="Google Review" className="w-[120px]" />
                  ) : (
                    Array.from({ length: testimonial.stars }).map((_, i) => <FaStar key={i} size={12} />)
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex items-center justify-between gap-4 px-4 lg:px-24">
          <button onClick={() => scrollToPage(activeIndex - 1)} disabled={activeIndex <= 0}>
            <MdChevronLeft size={40} />
          </button>
          <div className="flex gap-2">
            {Array.from({ length: pageCount }).map((_, i) => (
              <div
                key={i}
                className="size-2 rounded-full"
                style={{ backgroundColor: i === activeIndex ? '#2A9C8E' : '#A2D6CF' }}
              />
            ))}
          </div>
          <button onClick={() => scrollToPage(activeIndex + 1)} disabled={activeIndex >= pageCount - 1}>
            <MdChevronRight size={40} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
